/* TN HQ 1.2 — guided routine UI with release-gated cart handoff.
 * With cartEnabled, ocuVerified, or pricingApproved false, this remains a preview and makes no cart requests.
 * No customer tracking, local/session storage, automatic retries, or undocumented OCU events. */
(() => {
  'use strict';
  if (customElements.get('tn-routine-builder')) return;

  class TNRoutineBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.data = null;
      this.cartReview = null;
      this.actionMode = 'review';
      try {
        this.data = JSON.parse(this.querySelector('[data-rb-config]').textContent);
      } catch (error) {
        this.fail('The routine preview could not load. Please refresh the page.');
        return;
      }

      const api = globalThis.TNRoutineCart;
      if (api?.Session) {
        try {
          this.cartSession = new api.Session({
            fetch: globalThis.fetch.bind(globalThis),
            root: this.data.root || '/',
            currency: this.data.currency || 'USD',
            products: this.data.products,
            enabled: this.data.cartEnabled === true,
            ocuVerified: this.data.ocuVerified === true,
            pricingApproved: this.data.pricingApproved === true,
            privateQa: this.data.privateQa === true
          });
        } catch (error) {
          this.cartSession = null;
        }
      }

      this.state = {
        moisturizer: this.querySelector('[data-rb-moisture]:checked')?.value || null,
        sizes: {},
        oil: this.querySelector('[data-rb-oil-toggle]')?.checked === true,
        oilId: this.querySelector('[data-rb-oil-variant]')?.value || ''
      };
      for (const key of ['milk', 'leavein']) {
        const select = this.querySelector(`[data-rb-size="${key}"]`);
        this.state.sizes[key] = select ? select.value : '';
        const product = this.data.products[key];
        const allowed = product ? product.variants.filter(v => /^(8|16)\s*oz$/i.test(v.title)) : [];
        if (!allowed.some(v => v.available)) {
          const radio = this.querySelector(`[data-rb-moisture][value="${key}"]`);
          if (radio) {
            radio.disabled = true;
            radio.closest('.rb-choice').classList.add('is-unavailable');
          }
          if (select) select.disabled = true;
        }
      }

      this.addEventListener('change', event => this.changed(event), { signal: this.controller.signal });
      this.addEventListener('click', event => {
        if (event.target.matches('[data-rb-moisture]')) this.changed(event);
      }, { signal: this.controller.signal });
      this.querySelector('[data-rb-review]').addEventListener('click', () => this.primaryAction(), { signal: this.controller.signal });
      this.querySelector('[data-rb-oil-toggle]').disabled = !this.data.products.oil?.variants.some(v => v.available);
      this.querySelector('[data-rb-nojs]').hidden = true;
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    variant(key, id) {
      return this.data.products[key]?.variants.find(v => String(v.id) === String(id)) || null;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.data.locale || 'en-US', {
          style: 'currency',
          currency: this.data.currency || 'USD'
        }).format(cents / 100);
      } catch {
        return `${(cents / 100).toFixed(2)} ${this.data.currency || 'USD'}`;
      }
    }

    changed(event) {
      const el = event.target;
      if (el.matches('[data-rb-moisture]')) this.state.moisturizer = el.value;
      if (el.matches('[data-rb-size]')) {
        const key = el.dataset.rbSize;
        this.state.sizes[key] = el.value;
        this.state.moisturizer = key;
        this.querySelector(`[data-rb-moisture][value="${key}"]`).checked = true;
      }
      if (el.matches('[data-rb-oil-toggle]')) this.state.oil = el.checked;
      if (el.matches('[data-rb-oil-variant]')) this.state.oilId = el.value;
      this.cartReview = null;
      this.actionMode = 'review';
      this.querySelector('[data-rb-confirmation]').hidden = true;
      this.update();
    }

    selection() {
      const base = this.data.products.bars?.variants[0] || null;
      const moisture = this.state.moisturizer
        ? this.variant(this.state.moisturizer, this.state.sizes[this.state.moisturizer])
        : null;
      const oil = this.state.oil ? this.variant('oil', this.state.oilId) : null;
      return { base, moisture, oil };
    }

    cartSelection() {
      const { base, moisture, oil } = this.selection();
      const selected = [];
      if (base) selected.push({ key: 'bars', id: String(base.id) });
      if (moisture && this.state.moisturizer) selected.push({ key: this.state.moisturizer, id: String(moisture.id) });
      if (oil && this.state.oil) selected.push({ key: 'oil', id: String(oil.id) });
      return selected;
    }

    setButton(text, disabled = false) {
      const button = this.querySelector('[data-rb-review]');
      button.disabled = disabled || this.busy === true;
      button.replaceChildren(document.createTextNode(text + ' '), Object.assign(document.createElement('span'), { textContent: '→' }));
      button.lastElementChild.setAttribute('aria-hidden', 'true');
    }

    showConfirmation(title, messages = []) {
      const box = this.querySelector('[data-rb-confirmation]');
      box.replaceChildren();
      const strong = document.createElement('strong');
      strong.textContent = title;
      box.append(strong);
      for (const message of messages.filter(Boolean)) {
        const p = document.createElement('p');
        p.textContent = message;
        p.style.margin = '6px 0 0';
        box.append(p);
      }
      box.hidden = false;
    }

    update() {
      const { base, moisture, oil } = this.selection();

      this.querySelectorAll('.rb-choice').forEach(card => {
        card.classList.toggle('is-selected', card.dataset.rbCard === this.state.moisturizer);
      });
      this.querySelector('[data-rb-oil-options]').hidden = !this.state.oil;

      const list = this.querySelector('[data-rb-lines]');
      list.replaceChildren();
      const line = (name, detail, price) => {
        const li = document.createElement('li');
        const text = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = name;
        const sub = document.createElement('small');
        sub.textContent = detail;
        const amount = document.createElement('span');
        amount.textContent = price;
        text.append(title, sub);
        li.append(text, amount);
        list.append(li);
      };

      let value = this.querySelector('[data-rb-value]');
      if (!value) {
        value = document.createElement('p');
        value.className = 'rb-help';
        value.dataset.rbValue = '';
        const totalBox = this.querySelector('.rb-total');
        totalBox.parentNode.insertBefore(value, totalBox);
      }
      value.hidden = true;
      value.textContent = '';

      line('Shampoo + Conditioner Bar Duo', '1 duo · two wash-day bars', base ? this.money(base.price) : 'Unavailable');
      line(
        this.state.moisturizer === 'milk' ? 'Hair Milk' : this.state.moisturizer === 'leavein' ? 'Leave-In Conditioner' : 'Your moisture choice',
        moisture ? `1 bottle · ${moisture.title}` : 'Choose the feel your hair loves',
        moisture ? this.money(moisture.price) : '—'
      );

      line(
        'Hair Growth Oil',
        oil ? `1 bottle · ${oil.title}` : this.state.oil ? 'Choose a size and formula' : 'Optional · not added',
        oil ? this.money(oil.price) : '—'
      );

      const routinePrice = [base, moisture].reduce((sum, variant) => sum + (variant ? Number(variant.price) : 0), 0);
      const total = routinePrice + (oil ? Number(oil.price) : 0);
      this.querySelector('[data-rb-total]').textContent = this.money(total);

      let message = '';
      if (!base?.available) message = 'The Bar Duo is unavailable right now. This routine cannot be completed yet.';
      else if (!moisture) message = 'Choose your moisture to finish your routine.';
      else if (!moisture.available) message = 'That moisturizer size is unavailable. Please choose another size.';
      else if (this.state.oil && !oil) message = 'Choose your oil size and formula, or leave the oil out for now.';
      else if (oil && !oil.available) message = 'That oil option is unavailable. Please choose another option.';
      else if (this.cartSession?.ready) message = 'Your choices are ready for a live cart check.';
      else message = 'Your choices are ready to review.';

      this.ready = Boolean(base?.available && moisture?.available && (!this.state.oil || oil?.available));
      this.querySelector('[data-rb-status]').textContent = message;
      if (this.actionMode === 'view_cart') this.setButton('View my cart', false);
      else if (this.cartReview) this.setButton('Add routine to cart', !this.ready);
      else this.setButton(this.cartSession?.ready ? 'Check my cart + review' : 'Review my routine', !this.ready);
    }

    async primaryAction() {
      if (this.busy) return;
      if (this.actionMode === 'view_cart') {
        globalThis.location.assign(`${this.data.root || '/'}cart`);
        return;
      }
      if (!this.ready) return;
      if (!this.cartSession?.ready) {
        this.showConfirmation('A wash day that feels like you.', ['Your selection is ready. This design preview does not add products to your cart or place an order.']);
        this.querySelector('[data-rb-status]').textContent = 'Preview complete. Nothing has been added to your cart.';
        return;
      }
      this.busy = true;
      const controls = Array.from(this.querySelectorAll('input, select')).map(el => [el, el.disabled]);
      controls.forEach(([el]) => { el.disabled = true; });
      try {
        if (this.cartReview) await this.commitCart();
        else await this.reviewCart();
      } finally {
        controls.forEach(([el, disabled]) => { el.disabled = disabled; });
        this.busy = false;
        this.update();
      }
    }

    async reviewCart() {
      const selected = this.cartSelection();
      this.setButton('Checking your cart', true);
      this.querySelector('[data-rb-status]').textContent = 'Checking your cart, current prices and availability.';
      try {
        const snapshot = await this.cartSession.snapshot(selected);
        if (snapshot.status !== 'review' || !snapshot.plan) {
          this.showConfirmation('One more check is needed.', [snapshot.message || 'Your routine could not be checked yet.']);
          this.cartReview = null;
          return;
        }
        if (!snapshot.plan.ok) {
          this.showConfirmation('Your cart needs a quick look first.', snapshot.plan.issues.map(x => x.message));
          this.cartReview = null;
          this.querySelector('[data-rb-status]').textContent = 'Nothing was added. Review the note above before continuing.';
          return;
        }
        this.cartReview = { selected, signature: snapshot.plan.signature };
        const messages = [
          ...snapshot.plan.advisories.map(x => x.message),
          snapshot.plan.items.length ? 'Only the missing items shown by the cart check will be added.' : 'Your routine is already covered by what is in your cart.'
        ];
        this.showConfirmation('Your cart check is complete.', messages);
        this.querySelector('[data-rb-status]').textContent = snapshot.plan.items.length ? 'Review complete. Add the routine when you are ready.' : 'Your routine is already in your cart.';
        if (!snapshot.plan.items.length) {
          this.actionMode = 'view_cart';
          this.cartReview = null;
        }
      } catch (error) {
        this.cartReview = null;
        this.showConfirmation('We could not check your cart.', ['Nothing was added. Please try the review again.']);
        this.querySelector('[data-rb-status]').textContent = 'Cart check failed safely. Nothing was submitted.';
      } finally {
        this.update();
      }
    }

    async commitCart() {
      const review = this.cartReview;
      if (!review) return;
      this.setButton('Adding your routine', true);
      this.querySelector('[data-rb-status]').textContent = 'Adding only the reviewed items to your cart.';
      const result = await this.cartSession.commit(review.selected, review.signature);
      this.cartReview = null;

      if (result.status === 'added') {
        this.showConfirmation('Your routine is in your cart, Curlfriend.', ['Nothing already in your cart was replaced or duplicated.']);
        this.querySelector('[data-rb-status]').textContent = result.message;
        this.actionMode = 'view_cart';
        this.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cart: result.cart, source: 'tn-routine-builder' } }));
      } else if (result.status === 'already_complete') {
        this.showConfirmation('Your routine is already covered.', [result.message]);
        this.querySelector('[data-rb-status]').textContent = result.message;
        this.actionMode = 'view_cart';
      } else if (result.status === 'review_changed') {
        this.showConfirmation('Your cart changed since the last check.', ['Review the updated cart before adding anything. Nothing was submitted.']);
        this.querySelector('[data-rb-status]').textContent = result.message;
        this.actionMode = 'review';
      } else if (result.status === 'blocked') {
        this.showConfirmation('Your cart needs a quick look first.', (result.plan?.issues || result.issues || []).map(x => x.message));
        this.querySelector('[data-rb-status]').textContent = 'Nothing was added.';
        this.actionMode = 'review';
      } else if (result.status === 'uncertain' || result.status === 'check_cart') {
        this.showConfirmation('Please check your cart before trying again.', [result.message]);
        this.querySelector('[data-rb-status]').textContent = result.message;
        this.actionMode = 'view_cart';
      } else {
        this.showConfirmation('We could not update your cart.', [result.message || 'Nothing was submitted. Please review your routine again.']);
        this.querySelector('[data-rb-status]').textContent = result.message || 'Cart update stopped safely.';
        this.actionMode = 'review';
      }
      this.update();
    }

    fail(message) {
      const status = this.querySelector('[data-rb-status]');
      if (status) status.textContent = message;
      const button = this.querySelector('[data-rb-review]');
      if (button) button.disabled = true;
    }
  }

  customElements.define('tn-routine-builder', TNRoutineBuilder);
})();
